/* istanbul ignore file */
import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import {
  utils,
  ListingPopupCard,
  withApplicationContext,
  MapPin,
} from '@adc/ui-adc'
import { getMarkerPopupPixelOffset } from '../../util/redesign/mapUtils'
import { isMobile } from '../../util/osDetector'
import { trackMapPin } from '../../services/analytics'
import { useIsHovered, useSetHover } from '../searchPanel/hoverContext'
import styles from './index.scss'
import { getMapPinTrackingInfo, mapPinTrackingType } from './helpers'

const cx = classnames.bind(styles)

const { getDisplayIndicator } = utils
const ICON_Z_INDEX_DEFAULT = 1
const ICON_Z_INDEX_PRIORITY = 1000
const BASE_DIMENSION = 17
const BASE_HOVER_DIMENSION = BASE_DIMENSION * 2.8
const ICON_DIMENSIONS = [BASE_DIMENSION, BASE_DIMENSION]
const ICON_DIMENSIONS_HOVER = [BASE_HOVER_DIMENSION, BASE_HOVER_DIMENSION]
const ICON_ANCHOR = [BASE_DIMENSION / 2, BASE_DIMENSION / 2]
const ICON_ANCHOR_HOVER = [BASE_HOVER_DIMENSION / 2, BASE_HOVER_DIMENSION / 2]
const BRIDGE_BELOW_OFFSET = -20 // Bridge at bottom edge when popup is below marker
const BRIDGE_ABOVE_OFFSET = -70 // Bridge at top edge when popup is above marker (halo height)
const LABEL_LEFT_OFFSET = 6
const LABEL_TOP_OFFSET = 10
const DEFAULT_COLOR = '#97a0a6' // $utk-color-gray-medium

const Marker = ({
  listing,
  location,
  onMarkerClick,
  index,
  map,
  bounds,
  applicationContext,
  featureFlags,
}) => {
  const zoom = map?.getZoom() || 0
  const isMobileOsRef = useRef(isMobile())
  const markerRef = useRef(null)

  const isCancelled =
    listing?.listing_status_group === graph.enums.ListingStatusGroup.Canceled

  const onClick = useCallback(
    arg => {
      let analyticsData = {}

      /**
       * NOTE - we only get analyticsData currently if user clicks on popup.  Clicking on
       * pin dot does not send this data.
       * TODO: enhance to get that data sent as well if applicable
       */
      const isDomEvent = typeof arg?.stopPropagation === 'function'
      if (isDomEvent) {
        arg.stopPropagation()
      } else {
        analyticsData = arg
      }
      isMobileOsRef.current = isMobile()
      if (isCancelled && !isMobileOsRef.current) {
        if (isDomEvent) arg.preventDefault()
        return
      }
      // Desktop previously navigated via an <a href target="_blank"> wrapping
      // the marker. That anchor was removed because focusable children crash
      // AdvancedMarkerElement ("Focusable child elements are not supported" ->
      // recursive addEventListener -> Maximum call stack size exceeded once
      // Sentry also wraps addEventListener). Open the PDP here instead, and only
      // on a real DOM click so we don't double-open when the popup card also
      // invokes this handler directly.
      if (isDomEvent && !isMobileOsRef.current && listing.listing_page_path) {
        window.open(listing.listing_page_path, '_blank', 'noopener,noreferrer')
      }
      onMarkerClick(listing.listing_id)
      trackMapPin({
        listing,
        isMobileView: isMobileOsRef.current,
        featureFlags: applicationContext.featureFlags,
        ...analyticsData,
      })
    },
    [listing, onMarkerClick, isCancelled, isMobileOsRef, applicationContext]
  )
  const isHovered = useIsHovered(listing.listing_id)
  const setHover = useSetHover()
  const onMouseOver = useCallback(() => {
    setHover(listing.listing_id, 'map')
  }, [setHover, listing?.listing_id])

  const onMouseOut = useCallback(() => {
    setHover(0, 'map')
  }, [setHover])

  const assetInfo = getDisplayIndicator(listing)

  const marker = {
    scaledSize: new window.google.maps.Size(...ICON_DIMENSIONS),
    anchor: new window.google.maps.Point(...ICON_ANCHOR),
  }

  const markerHovered = {
    scaledSize: new window.google.maps.Size(...ICON_DIMENSIONS_HOVER),
    anchor: new window.google.maps.Point(...ICON_ANCHOR_HOVER),
  }

  const popupOptions = bounds
    ? getMarkerPopupPixelOffset(location, bounds, zoom)
    : null

  const mapPinTrackingInfo = getMapPinTrackingInfo(
    listing,
    applicationContext.featureFlags
  )

  let label = ''
  if (mapPinTrackingInfo[mapPinTrackingType.UPDATED]) {
    label = mapPinTrackingType.UPDATED
  } else if (mapPinTrackingInfo[mapPinTrackingType.NEW]) {
    label = mapPinTrackingType.NEW
  }

  const labelColor = mapPinTrackingInfo[mapPinTrackingType.SEEN]
    ? assetInfo?.colorDarkFaded
    : assetInfo?.colorDark || DEFAULT_COLOR

  // hovered get's priority and if not hovered, markers with labels get priority
  const zIndexForHovers = isHovered
    ? ICON_Z_INDEX_PRIORITY * 2
    : ICON_Z_INDEX_PRIORITY * (label ? 2 : 1) - index - 1 ||
      ICON_Z_INDEX_DEFAULT

  const markerLeft = 0 - markerHovered.scaledSize.width / 2
  const markerTop = 0 - markerHovered.scaledSize.height / 2
  const dotLeft = (markerHovered.scaledSize.width - marker.scaledSize.width) / 2
  const dotTop =
    (markerHovered.scaledSize.height - marker.scaledSize.height) / 2
  // Center the halo on the dot within the same positioned container so the
  // dot's/container's borders offset both identically -> concentric shade.
  const haloLeft =
    dotLeft + (marker.scaledSize.width - markerHovered.scaledSize.width) / 2
  const haloTop =
    dotTop + (marker.scaledSize.height - markerHovered.scaledSize.height) / 2

  return (
    <>
      <div
        // Intentionally a non-focusable <div>, not an <a href>. AdvancedMarker
        // content must not contain focusable elements (see onClick above).
        // Navigation is handled in onClick via window.open.
        ref={markerRef}
        className={cx('marker-container', { 'not-allowed': isCancelled })}
        style={{
          left: `${markerLeft}px`,
          top: `${markerTop}px`,
          zIndex: zIndexForHovers,
        }}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseOut}
        onClick={onClick}
      >
        <div
          className={cx('halo', {
            active: isHovered,
            inactive: isHovered === false, // null is for initial state
          })}
          style={{
            width: markerHovered.scaledSize.width,
            height: markerHovered.scaledSize.height,
            left: `${haloLeft}px`,
            top: `${haloTop}px`,
            zIndex: zIndexForHovers,
          }}
        />
        {!isMobileOsRef.current && (
          <div
            className={cx('bridge', {
              active: isHovered,
              inactive: isHovered === false, // null is for initial state
            })}
            style={{
              bottom: !popupOptions?.alignBottom
                ? `${BRIDGE_ABOVE_OFFSET}px`
                : `${BRIDGE_BELOW_OFFSET}px`,
            }}
          />
        )}
        <MapPin
          isHovered={isHovered}
          listingId={listing.listing_id}
          dotAssetType={listing?.listing_configuration?.asset_type}
          seen={mapPinTrackingInfo[mapPinTrackingType.SEEN]}
          style={{
            top: `${dotTop}px`,
            left: `${dotLeft}px`,
            width: marker.scaledSize.width,
            height: marker.scaledSize.height,
          }}
        />
        {isHovered && popupOptions && !isMobileOsRef.current && (
          <div
            className={cx('info-card-wrapper')}
            style={{
              left: `${popupOptions?.pixelOffset?.width}px`,
              top: `${popupOptions?.pixelOffset?.height}px`,
              zIndex: zIndexForHovers,
            }}
          >
            <ListingPopupCard
              className={cx('info-card', {
                active: isHovered,
                inactive: isHovered === false, // null is for initial state
              })}
              linkProps={{
                onClick,
              }}
              listing={listing}
              elmId={`marker_popup_${listing.listing_id}`}
              isCancelled={isCancelled}
              featureFlags={featureFlags}
            />
          </div>
        )}
        {label && (
          <div
            className={cx('label')}
            data-elm-id='marker-label'
            style={{
              left: `${dotLeft + LABEL_LEFT_OFFSET}px`,
              top: `${dotTop - LABEL_TOP_OFFSET}px`,
              backgroundColor: labelColor,
              // Dual-track popup cards are taller and can reach the pin; keep the
              // label beneath the open popup so it doesn't bleed through.
              zIndex:
                isHovered && listing.dual_track_sibling_lobs?.length
                  ? zIndexForHovers - 1
                  : zIndexForHovers,
            }}
          >
            {label}
          </div>
        )}
      </div>
    </>
  )
}

Marker.propTypes = {
  index: PropTypes.number,
  listing: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  map: PropTypes.object,
  bounds: PropTypes.object,
  applicationContext: PropTypes.object.isRequired,
  featureFlags: PropTypes.object,
}

export default withApplicationContext(Marker)
